const baseUrl = import.meta.env.VITE_ADMIN_BFF_BASE_URL;

if (!baseUrl) {
    throw new Error("VITE_ADMIN_BFF_BASE_URL is not set.");
}

export interface AdminOverview {
    phase: number;
    status: string;
    tenantCount: number;
    activeTenantCount: number;
    suspendedTenantCount: number;
    userCount: number;
    domainCount: number;
    verifiedDomainCount: number;
    audit24hCount: number;
}

export interface Tenant {
    id: string;
    slug: string;
    displayName: string;
    description: string | null;
    status: "Active" | "Suspended" | "Archived";
    storageQuotaBytes: number;
    seatLimit: number;
}

export interface UpsertTenantPayload {
    slug: string;
    displayName: string;
    description?: string | null;
    seatLimit: number;
    storageQuotaBytes: number;
}

export interface TenantUser {
    id: string;
    tenantId: string;
    email: string;
    displayName: string;
    role: string;
    isEnabled: boolean;
}

export interface UpsertTenantUserPayload {
    email: string;
    displayName: string;
    role?: string;
    /** Initial password — required when creating (Identity policy:
     *  12+ chars, upper-case, digit, symbol), ignored on update. */
    password?: string;
    isEnabled: boolean;
}

export interface TenantStorage {
    tenantId: string;
    quotaBytes: number;
    usedBytes: number;
    objectCount: number;
    available: boolean;
}

export interface MailDomain {
    id: string;
    tenantId: string;
    domain: string;
    isVerified: boolean;
    mxHost: string | null;
    spfRecord: string | null;
    dkimSelector: string | null;
    /** Licenced seats: null = unlimited (legacy), 0 = no licence, >0 = cap. */
    seatLimit: number | null;
    /** Current number of identity users on this domain. */
    userCount: number;
    /** Licence plan label (Basic / Standard / Premium). Informational. */
    planTier: string | null;
    /** Per-mailbox email-storage ceiling (bytes) for this plan. Default 1 GiB. */
    mailQuotaBytesPerUser: number;
    /** Per-mailbox file-storage ceiling (bytes) for this plan. Default 1 GiB. */
    fileQuotaBytesPerUser: number;
    /** CSV of suite apps this plan grants (mail,drive,docs,sheet,slides,pdf,connect). */
    enabledApps: string;
    /** Office-network allowlist: CSV of IP/CIDR. Empty = no restriction. */
    accessAllowedCidrs: string;
    /** Whether native IMAP/SMTP client access is permitted for this domain. */
    allowImapSmtp: boolean;
    /** Allowed MFA methods: "authenticator" | "biometric" | "both". */
    mfaMethods: string;
    /** When true, users on this domain must complete a second factor to sign in. */
    requireMfa: boolean;
}

/** Canonical suite app keys a plan can grant. */
export const SUITE_APPS = ["mail", "drive", "docs", "sheet", "slides", "pdf", "connect"] as const;
export type SuiteApp = (typeof SUITE_APPS)[number];

/** Caller's admin scope — drives which parts of the console render. */
export interface Me {
    email: string;
    isSuperAdmin: boolean;
    isDomainAdmin: boolean;
    emailDomain: string;
    tenantId: string | null;
}

export interface UpsertDomainPayload {
    domain: string;
    mxHost?: string | null;
    spfRecord?: string | null;
    dkimSelector?: string | null;
}

/** A mail distribution list: Postfix fans mail for `email` out to `members`. */
export interface DistributionList {
    id: string;
    email: string;
    domain: string;
    displayName: string;
    active: boolean;
    createdAt: string;
    members: string[];
}

/** Create: email + members required. Update: null/omitted fields are left
 *  unchanged; a supplied members array replaces the full member set. */
export interface UpsertDistributionListPayload {
    email?: string;
    displayName?: string;
    active?: boolean;
    members?: string[];
}

export interface AuditEntry {
    id: string;
    tenantId: string | null;
    actorEmail: string;
    action: string;
    subject: string | null;
    details: string | null;
    occurredAt: string;
}

/**
 * A failed admin-bff call. `field` is set when the body was a field-level validation error —
 * `{ error, field }`, the shape Identity (and the BFF, for the mobile policy) uses — so a form can
 * show the message next to the field it names.
 */
export class AdminApiError extends Error {
    readonly status: number;
    readonly field: string | null;

    constructor(message: string, status: number, field: string | null) {
        super(message);
        this.name = "AdminApiError";
        this.status = status;
        this.field = field;
    }
}

async function fetchJson<T>(
    path: string,
    accessToken: string | undefined,
    init: RequestInit = {}
): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set("Accept", "application/json");
    if (init.body) headers.set("Content-Type", "application/json");
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    const res = await fetch(new URL(path, baseUrl).toString(), { ...init, headers });
    if (!res.ok) {
        // Prefer the ProblemDetails message the BFF sends with validation
        // failures (e.g. "member has no mailbox") over a bare status line.
        // Mobile-policy validation errors arrive as `{ error, field }` instead.
        let detail = "";
        let field: string | null = null;
        try {
            const body = (await res.json()) as { title?: string; detail?: string; error?: unknown; field?: unknown };
            detail = body.detail ?? body.title ?? (typeof body.error === "string" ? body.error : "");
            if (typeof body.field === "string" && body.field) field = body.field;
        } catch {
            /* body wasn't JSON */
        }
        throw new AdminApiError(detail || `Admin BFF ${path} → ${res.status} ${res.statusText}`, res.status, field);
    }
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
}

export const adminApi = {
    overview: (token: string | undefined) => fetchJson<AdminOverview>("/api/overview", token),

    me: (token: string | undefined) => fetchJson<Me>("/api/me", token),

    listTenants: (token: string | undefined) => fetchJson<Tenant[]>("/api/tenants", token),
    createTenant: (payload: UpsertTenantPayload, token: string | undefined) =>
        fetchJson<Tenant>("/api/tenants", token, { method: "POST", body: JSON.stringify(payload) }),
    updateTenant: (id: string, payload: UpsertTenantPayload, token: string | undefined) =>
        fetchJson<Tenant>(`/api/tenants/${id}`, token, { method: "PUT", body: JSON.stringify(payload) }),
    suspendTenant: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${id}/suspend`, token, { method: "POST" }),
    reactivateTenant: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${id}/reactivate`, token, { method: "POST" }),
    deleteTenant: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${id}`, token, { method: "DELETE" }),

    listUsers: (tenantId: string, token: string | undefined) =>
        fetchJson<TenantUser[]>(`/api/tenants/${tenantId}/users`, token),
    createUser: (tenantId: string, payload: UpsertTenantUserPayload, token: string | undefined) =>
        fetchJson<TenantUser>(`/api/tenants/${tenantId}/users`, token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    updateUser: (tenantId: string, userId: string, payload: UpsertTenantUserPayload, token: string | undefined) =>
        fetchJson<TenantUser>(`/api/tenants/${tenantId}/users/${userId}`, token, {
            method: "PUT", body: JSON.stringify(payload)
        }),
    deleteUser: (tenantId: string, userId: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${tenantId}/users/${userId}`, token, { method: "DELETE" }),

    listDomains: (tenantId: string, token: string | undefined) =>
        fetchJson<MailDomain[]>(`/api/tenants/${tenantId}/domains`, token),
    createDomain: (tenantId: string, payload: UpsertDomainPayload, token: string | undefined) =>
        fetchJson<MailDomain>(`/api/tenants/${tenantId}/domains`, token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    verifyDomain: (tenantId: string, domainId: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${tenantId}/domains/${domainId}/verify`, token, { method: "POST" }),
    setDomainSeatLimit: (
        tenantId: string,
        domainId: string,
        seatLimit: number | null,
        planTier: string | null,
        token: string | undefined,
        // Optional per-plan storage quotas (bytes/mailbox). Omitted (undefined)
        // fields are left unchanged server-side.
        mailQuotaBytesPerUser?: number | null,
        fileQuotaBytesPerUser?: number | null,
        // Optional CSV of granted apps; omitted leaves it unchanged.
        enabledApps?: string | null
    ) =>
        fetchJson<void>(`/api/tenants/${tenantId}/domains/${domainId}/seat-limit`, token, {
            method: "PUT",
            body: JSON.stringify({ seatLimit, planTier, mailQuotaBytesPerUser, fileQuotaBytesPerUser, enabledApps })
        }),
    // Security controls (office-network allowlist + IMAP/SMTP toggle). Posts to
    // the same domain endpoint; unset licence/quota fields are left unchanged.
    setDomainSecurity: (
        tenantId: string,
        domainId: string,
        accessAllowedCidrs: string,
        allowImapSmtp: boolean,
        mfaMethods: string,
        requireMfa: boolean,
        token: string | undefined
    ) =>
        fetchJson<void>(`/api/tenants/${tenantId}/domains/${domainId}/security`, token, {
            method: "PUT",
            body: JSON.stringify({ accessAllowedCidrs, allowImapSmtp, mfaMethods, requireMfa })
        }),
    deleteDomain: (tenantId: string, domainId: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${tenantId}/domains/${domainId}`, token, { method: "DELETE" }),

    // Distribution lists (group addresses, expanded by Postfix). Domain-scoped
    // rather than tenant-scoped: the BFF confines TenantAdmins to their own
    // domain regardless of the filter passed.
    listDistributionLists: (domain: string | undefined, token: string | undefined) =>
        fetchJson<DistributionList[]>(
            `/api/distribution-lists${domain ? `?domain=${encodeURIComponent(domain)}` : ""}`,
            token
        ),
    listDlDomains: (token: string | undefined) =>
        fetchJson<string[]>("/api/distribution-lists/domains", token),
    createDistributionList: (payload: UpsertDistributionListPayload, token: string | undefined) =>
        fetchJson<DistributionList>("/api/distribution-lists", token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    updateDistributionList: (id: string, payload: UpsertDistributionListPayload, token: string | undefined) =>
        fetchJson<DistributionList>(`/api/distribution-lists/${id}`, token, {
            method: "PUT", body: JSON.stringify(payload)
        }),
    deleteDistributionList: (id: string, token: string | undefined) =>
        fetchJson<void>(`/api/distribution-lists/${id}`, token, { method: "DELETE" }),

    listAudit: (tenantId: string | undefined, token: string | undefined) =>
        fetchJson<AuditEntry[]>(
            `/api/audit${tenantId ? `?tenantId=${tenantId}` : ""}`,
            token
        ),

    getTenantStorage: (tenantId: string, token: string | undefined) =>
        fetchJson<TenantStorage>(`/api/tenants/${tenantId}/storage`, token),

    // Per-tenant Connect feature policy.
    getConnectPolicy: (tenantId: string, token: string | undefined) =>
        fetchJson<ConnectPolicy>(`/api/tenants/${tenantId}/connect-policy`, token),
    updateConnectPolicy: (tenantId: string, payload: ConnectPolicy, token: string | undefined) =>
        fetchJson<ConnectPolicy>(`/api/tenants/${tenantId}/connect-policy`, token, {
            method: "PUT",
            body: JSON.stringify(payload)
        }),

    // SAML identity providers (phase 1: registration + validation).
    listSamlIdps: (tenantId: string, token: string | undefined) =>
        fetchJson<SamlIdentityProvider[]>(`/api/tenants/${tenantId}/saml-idps`, token),
    createSamlIdp: (tenantId: string, payload: UpsertSamlIdpPayload, token: string | undefined) =>
        fetchJson<SamlIdentityProvider>(`/api/tenants/${tenantId}/saml-idps`, token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    updateSamlIdp: (tenantId: string, id: string, payload: UpsertSamlIdpPayload, token: string | undefined) =>
        fetchJson<SamlIdentityProvider>(`/api/tenants/${tenantId}/saml-idps/${id}`, token, {
            method: "PUT", body: JSON.stringify(payload)
        }),
    validateSamlIdp: (tenantId: string, id: string, token: string | undefined) =>
        fetchJson<SamlIdentityProvider>(`/api/tenants/${tenantId}/saml-idps/${id}/validate`, token, {
            method: "POST"
        }),
    deleteSamlIdp: (tenantId: string, id: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${tenantId}/saml-idps/${id}`, token, { method: "DELETE" }),
    // Per-registration, not global: each registration has its own ACS URL (see
    // SamlIdentityProvider's class remarks), so the metadata to hand an IdP names that
    // registration's real endpoint — entity ID/certificate stay shared regardless.
    getSpMetadataSummary: (id: string, token: string | undefined) =>
        fetchJson<SpMetadataSummary>(`/api/saml/sp-metadata/${id}/summary`, token),

    // SAML service providers — the reverse role: third-party apps (Salesforce, Slack, AWS IAM
    // Identity Center) that trust Athavita as their IdP.
    listSamlSps: (tenantId: string, token: string | undefined) =>
        fetchJson<SamlServiceProvider[]>(`/api/tenants/${tenantId}/saml-sps`, token),
    createSamlSp: (tenantId: string, payload: UpsertSamlSpPayload, token: string | undefined) =>
        fetchJson<SamlServiceProvider>(`/api/tenants/${tenantId}/saml-sps`, token, {
            method: "POST", body: JSON.stringify(payload)
        }),
    updateSamlSp: (tenantId: string, id: string, payload: UpsertSamlSpPayload, token: string | undefined) =>
        fetchJson<SamlServiceProvider>(`/api/tenants/${tenantId}/saml-sps/${id}`, token, {
            method: "PUT", body: JSON.stringify(payload)
        }),
    validateSamlSp: (tenantId: string, id: string, token: string | undefined) =>
        fetchJson<SamlServiceProvider>(`/api/tenants/${tenantId}/saml-sps/${id}/validate`, token, {
            method: "POST"
        }),
    deleteSamlSp: (tenantId: string, id: string, token: string | undefined) =>
        fetchJson<void>(`/api/tenants/${tenantId}/saml-sps/${id}`, token, { method: "DELETE" }),
    getIdpMetadataSummary: (id: string, token: string | undefined) =>
        fetchJson<IdpMetadataSummary>(`/api/saml/idp-metadata/${id}/summary`, token)
};

/** IdP metadata XML isn't JSON, so it bypasses fetchJson — used by the "Download metadata"
 * button on the Service Providers screen. */
export async function downloadIdpMetadataXml(id: string, accessToken: string | undefined): Promise<string> {
    const headers = new Headers({ Accept: "application/samlmetadata+xml" });
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    const res = await fetch(new URL(`/api/saml/idp-metadata/${id}`, baseUrl).toString(), { headers });
    if (!res.ok) throw new Error(`Admin BFF /api/saml/idp-metadata/${id} → ${res.status} ${res.statusText}`);
    return res.text();
}

/** SP metadata XML isn't JSON, so it bypasses fetchJson — used by the
 * "Download metadata" button. */
export async function downloadSpMetadataXml(id: string, accessToken: string | undefined): Promise<string> {
    const headers = new Headers({ Accept: "application/samlmetadata+xml" });
    if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`);
    const res = await fetch(new URL(`/api/saml/sp-metadata/${id}`, baseUrl).toString(), { headers });
    if (!res.ok) throw new Error(`Admin BFF /api/saml/sp-metadata/${id} → ${res.status} ${res.statusText}`);
    return res.text();
}

// SAML 2.0 SSO. See EnterpriseIdentity/service/Data/SamlIdentityProvider.cs for the full
// field-level rationale this mirrors.
export type SamlProvider = "Okta" | "PingOne" | "JumpCloud" | "AzureAd" | "Generic";
export const SAML_PROVIDERS: SamlProvider[] = ["Okta", "PingOne", "JumpCloud", "AzureAd", "Generic"];

export interface SamlIdentityProvider {
    id: string;
    tenantId: string;
    provider: SamlProvider;
    displayName: string;
    buttonLabel: string;
    metadataUrl: string | null;
    metadataXml: string | null;
    idpEntityId: string | null;
    idpSsoUrl: string | null;
    idpSsoBinding: string | null;
    idpSigningCertificate: string | null;
    idpCertificateExpiresAt: string | null;
    emailClaim: string;
    autoProvisionUsers: boolean;
    enabled: boolean;
    lastValidationError: string | null;
    lastValidatedAt: string | null;
    createdAt: string;
    updatedAt: string;
    updatedBy: string | null;
}

export interface UpsertSamlIdpPayload {
    provider: SamlProvider;
    displayName: string;
    buttonLabel: string | null;
    metadataUrl: string | null;
    metadataXml: string | null;
    emailClaim: string | null;
    autoProvisionUsers: boolean;
    enabled: boolean;
}

export interface SpMetadataSummary {
    entityId: string;
    acsUrl: string;
    certificateThumbprint: string;
    certificateExpiresAt: string;
}

// SAML service providers — the reverse role. See
// EnterpriseIdentity/service/Data/SamlServiceProvider.cs for the full field-level rationale.
export interface SamlServiceProvider {
    id: string;
    tenantId: string;
    displayName: string;
    metadataUrl: string | null;
    metadataXml: string | null;
    spEntityId: string | null;
    acsUrl: string | null;
    acsBinding: string | null;
    spSigningCertificate: string | null;
    enabled: boolean;
    lastValidationError: string | null;
    lastValidatedAt: string | null;
    createdAt: string;
    updatedAt: string;
    updatedBy: string | null;
}

export interface UpsertSamlSpPayload {
    displayName: string;
    metadataUrl: string | null;
    metadataXml: string | null;
    enabled: boolean;
}

export interface IdpMetadataSummary {
    entityId: string;
    ssoUrl: string;
    certificateThumbprint: string;
    certificateExpiresAt: string;
}

export interface ConnectPolicy {
    tenantId: string;
    allowRecording: boolean;
    allowFileSharing: boolean;
    allowIncomingWebhooks: boolean;
    allowChannelTabs: boolean;
    allowFederation: boolean;
    updatedAt?: string | null;
}

// ── Mobile devices & app protection (Mobile/docs/phase0-contract.md §4 "Admin", §5) ──
// The admin-bff proxies these to the Identity service; field names follow the contract.

/** `mail` | `connect` | `authenticator` (contract §0). Kept as string so an app added later still renders. */
export type MobileAppKey = string;
/** Enum order matters for "most restrictive wins": allowed < blockedToUnmanaged < blocked. */
export type ShareRule = "allowed" | "blockedToUnmanaged" | "blocked";
/** off < warn < block. */
export type AttestationMode = "off" | "warn" | "block";
/** off < report < enforce. */
export type PinningMode = "off" | "report" | "enforce";

export interface MobileDevice {
    deviceId: string;
    /** Admin-list extras — not spelled out by the contract's device shape, rendered when present. */
    tenantId?: string | null;
    userId?: string | null;
    userEmail?: string | null;
    userDisplayName?: string | null;
    app?: MobileAppKey | null;
    /** `ios` | `android`. */
    platform?: string | null;
    name?: string | null;
    model?: string | null;
    osVersion?: string | null;
    appVersion?: string | null;
    createdAt?: string | null;
    lastSeenAt?: string | null;
    /** `active` | `revoked` | `wiped`, plus a wipe-pending state between the admin command and the device's check-in. */
    status?: string | null;
    /** `verified` | `unverified` | `failed`. */
    attestationStatus?: string | null;
    attestationReason?: string | null;
}

export interface MobileDeviceList {
    items: MobileDevice[];
    /** Total matching devices when Identity reports it; null when it returns a bare array. */
    total?: number | null;
    skip: number;
    take: number;
}

export interface MobileDeviceQuery {
    /** Omitted = all tenants (SuperAdmin only — the BFF pins TenantAdmins to their own tenant). */
    tenantId?: string | null;
    userId?: string | null;
    /** Identity's device status: `active` | `revoked` | `wipe_pending` | `wiped`. Omitted = any. Filtered
     *  server-side, so paging and `total` reflect it. */
    status?: string | null;
    skip: number;
    take: number;
}

export interface PlatformVersions {
    ios: string;
    /** Android API level as a string, e.g. "29" = Android 10. */
    android: string;
}

/** The `policy` object of contract §5 — the body of `PUT /api/admin/mobile/policy`. */
export interface MobilePolicy {
    requireAppPin: boolean;
    allowBiometric: boolean;
    pinLength: number;
    pinRetryLimit: number;
    reAuthAfterMinutes: number;
    blockJailbrokenRooted: boolean;
    minOsVersion: PlatformVersions;
    minAppVersion: PlatformVersions;
    blockClipboardOut: ShareRule;
    blockShareOut: ShareRule;
    blockSaveToLocal: boolean;
    blockScreenshot: boolean;
    blockBackup: boolean;
    allowOpenInUnmanaged: boolean;
    requireManagedBrowser: boolean;
    offlineGraceHours: number;
    maxOfflineDays: number;
    wipeOnPolicyFail: boolean;
    networkAllowlistCidrs: string[];
    attestationMode: AttestationMode;
    pinningMode: PinningMode;
    allowedAccounts: string[];
}

/** What the BFF returns for GET/PUT policy: the tenant's policy plus its signed version/etag. */
export interface MobilePolicyEnvelope {
    tenantId: string;
    version?: number | null;
    etag?: string | null;
    updatedAt?: string | null;
    updatedBy?: string | null;
    /** Partial so a field Identity doesn't send yet falls back to the contract default in the editor. */
    policy: Partial<MobilePolicy>;
}

export type MobileDeviceCommand = "wipe" | "revoke";

export const mobileApi = {
    listDevices: (q: MobileDeviceQuery, token: string | undefined) => {
        const params = new URLSearchParams({ skip: String(q.skip), take: String(q.take) });
        if (q.tenantId) params.set("tenantId", q.tenantId);
        if (q.userId) params.set("userId", q.userId);
        if (q.status) params.set("status", q.status);
        return fetchJson<MobileDeviceList>(`/api/admin/mobile/devices?${params.toString()}`, token);
    },
    /** `tenantId` lets the BFF verify a TenantAdmin owns the device; SuperAdmins may omit it. */
    deviceCommand: (
        deviceId: string,
        command: MobileDeviceCommand,
        tenantId: string | null | undefined,
        token: string | undefined
    ) =>
        fetchJson<unknown>(
            `/api/admin/mobile/devices/${encodeURIComponent(deviceId)}/${command}` +
                (tenantId ? `?tenantId=${encodeURIComponent(tenantId)}` : ""),
            token,
            { method: "POST" }
        ),
    getPolicy: (tenantId: string, token: string | undefined) =>
        fetchJson<MobilePolicyEnvelope>(`/api/admin/mobile/policy?tenantId=${encodeURIComponent(tenantId)}`, token),
    /** Resolves undefined if Identity answered 204 without the saved policy. */
    updatePolicy: (tenantId: string, policy: MobilePolicy, token: string | undefined) =>
        fetchJson<MobilePolicyEnvelope | undefined>(`/api/admin/mobile/policy?tenantId=${encodeURIComponent(tenantId)}`, token, {
            method: "PUT",
            body: JSON.stringify(policy)
        })
};

// Kept for backward compatibility with the old dashboard card.
export async function fetchOverview(accessToken: string): Promise<AdminOverview> {
    return adminApi.overview(accessToken);
}
